/**
 * Labstep
 *
 * @module grid/NewIndex/coldefs/metadata/date
 * @desc Column Definition for metadata date
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { isValid, parse, parseISO } from 'date-fns';
import { ICONS } from 'labstep-web/constants/icons';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type { CellRendererParams } from 'labstep-web/core/DataGrid/types';
import {
  createOrUpdateValue,
  setValue,
} from 'labstep-web/core/DataGrid/utils';
import { FormShowEditDateToggleComponent } from 'labstep-web/core/Form/ShowEdit/Date/ToggleComponent';
import type { IFormShowEditDateToggleComponentProps } from 'labstep-web/core/Form/ShowEdit/Date/ToggleComponent/types';
import { TextPlaceholder } from 'labstep-web/core/Text/Placeholder';
import { getMetadata } from 'labstep-web/grid/NewIndex/coldefs';
import type { NewIndexColDef } from 'labstep-web/grid/NewIndex/coldefs/types';
import { GridNewIndexColumnHeader } from 'labstep-web/grid/NewIndex/components/ColumnHeader';
import { GridNewIndexService } from 'labstep-web/grid/NewIndex/services/grid-new-index.service';
import { GridColumnDefinitionService } from 'labstep-web/grid/services/grid-column-definition.service';
import { PostFilterFieldTypes } from 'labstep-web/grid/services/grid-filter.service';
import type { Entity } from 'labstep-web/models/entity.model';
import { Metadata } from 'labstep-web/models/metadata';
import type { MetadataType } from 'labstep-web/models/metadata/types';
import { MetadataThread } from 'labstep-web/models/metadata-thread.model';
import {
  dateOnlyAtUTC,
  formatDateOnly,
} from 'labstep-web/services/date.service';
import rules from 'labstep-web/services/validation/rules';

export const getMetadataDateColDef = (
  parentEntityClass: string,
  type: MetadataType,
  metadataLabel: string,
  entityName?: string,
): NewIndexColDef => {
  const setMetadataDate = (
    params: ValueSetterParams | CellRendererParams<Entity>,
    newValue: string | null,
  ) => {
    if (!params.node) {
      return;
    }

    const metadata = getMetadata(
      parentEntityClass,
      params.node.data,
      metadataLabel,
      type,
    );
    const metadataThreadId =
      parentEntityClass !== params.node.data.entityName
        ? params.node.data[parentEntityClass].metadata_thread.idAttr
        : params.node.data.metadata_thread.idAttr;

    if (!metadata) {
      if (
        parentEntityClass !== params.node.data.entityName &&
        !params.node.data[parentEntityClass]
      ) {
        return false;
      }
      const props = {
        parentName: MetadataThread.entityName,
        parentId: metadataThreadId,
      };

      createOrUpdateValue({
        entityName: Metadata.entityName,
        body: {
          type,
          label: metadataLabel,
          metadata_thread_id: metadataThreadId,
          date: newValue,
        },
        createProps: props,
        options: GridColumnDefinitionService.getOptions(params),
      });
    } else if (newValue === null) {
      setValue(
        'date',
        null,
        {
          entityName: metadata.entityName,
          id: metadata.idAttr,
        },
        rules.metadata.date,
        GridColumnDefinitionService.getOptions(params),
      );
    } else {
      setValue(
        'date',
        newValue,
        {
          entityName: metadata.entityName,
          id: metadata.idAttr,
        },
        rules.metadata.date,
        GridColumnDefinitionService.getOptions(params),
      );
    }
  };

  return {
    colId: `metadata:${parentEntityClass}:${type}:${metadataLabel}`,
    sortable: true,
    headerName: metadataLabel,
    headerComponent: GridNewIndexColumnHeader,
    headerComponentParams: {
      icon: ICONS.metadata.secondary,
      cornerIcon:
        ICONS[parentEntityClass as keyof typeof ICONS].primary,
      postFilterSettings: {
        path:
          parentEntityClass !== entityName
            ? `${parentEntityClass}.metadatas`
            : 'metadatas',
        field: 'date',
        entityName: Metadata.entityName,
        fieldType: PostFilterFieldTypes.metadata,
      },
    },
    editable: (params: EditableCallbackParams) => {
      const entity = params.node.data;
      if (
        parentEntityClass === 'purchase_order' &&
        !entity.purchase_order
      ) {
        return false;
      }
      return GridNewIndexService.getEditable(params);
    },
    valueGetter: (params: ValueGetterParams) => {
      if (!params.node) {
        return null;
      }
      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );

      if (!metadata) {
        return null;
      }
      return metadata.date;
    },
    valueSetter: (params: ValueSetterParams) => {
      if (!params.node) {
        return false;
      }

      if (params.newValue == null) {
        setMetadataDate(params, null);
        return true;
      }

      const dateISO = parseISO(params.newValue);
      if (isValid(dateISO)) {
        const newValue = formatDateOnly(dateISO);
        setMetadataDate(params, newValue);
        return true;
      }

      const date = parse(params.newValue, 'mm/dd/yyyy', new Date());
      if (isValid(date)) {
        const newValue = formatDateOnly(date);
        setMetadataDate(params, newValue);
        return true;
      }

      return false;
    },
    cellRenderer: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }

      const entity = params.node.data;
      if (
        parentEntityClass === 'purchase_order' &&
        !entity.purchase_order
      ) {
        return null;
      }

      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );

      if (!metadata || !metadata.date) {
        return (
          <DataGridPlaceholder
            params={params}
            editable={GridNewIndexService.getEditable(params)}
            placeholder="Enter"
          />
        );
      }

      return <>{dateOnlyAtUTC(metadata.date)}</>;
    },
    cellEditorPopup: true,
    cellEditorPopupPosition: 'under',
    cellEditor: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }
      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );

      const onCloseFn: IFormShowEditDateToggleComponentProps['onClose'] =
        (date): void => {
          if (!date || date.length !== 1) {
            return;
          }

          const newValue = formatDateOnly(date[0]);

          setMetadataDate(params, newValue);
        };

      return (
        <FormShowEditDateToggleComponent
          canEdit
          dateTimePickerOnly
          viewComponent={<TextPlaceholder></TextPlaceholder>}
          defaultDate={metadata?.date || null}
          onClose={onCloseFn}
          dateOnly
        />
      );
    },
    suppressKeyboardEvent: suppressKeyboardEventClearCells,
    valueClearer: () => null,
  };
};
